import * as React from 'react';
import {
  Box,
  TextField,
  Typography,
  Stack,
  Avatar,
  IconButton,
  useTheme,
  Tooltip
} from '@mui/material';
import PhotoCameraBackTwoToneIcon from '@mui/icons-material/PhotoCameraBackTwoTone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAppDispatch } from '../../../redux-store/hooks';
import { createCategory, updateCategory } from '../../../services';
import { PropSelectorDialog, CommonBtn } from '../../../components';
import { ICategoryType } from '../../../all-types';

interface AddCategoryProps {
  open: boolean;
  toggleDailog: (val: boolean) => void;
  editData?: ICategoryType;
}

const AddCategory: React.FC<AddCategoryProps> = ({ toggleDailog, editData }) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  
  const [propDialogOpen, setPropDialogOpen] = React.useState(false);
  const [selectedProp, setSelectedProp] = React.useState<string>(editData?.categoryIcon || '');
  const [categoryName, setCategoryName] = React.useState<string>(editData?.categoryName || '');

  // Reset form when editData changes
  React.useEffect(() => {
    setCategoryName(editData?.categoryName || '');
    setSelectedProp(editData?.categoryIcon || '');
  }, [editData]);

  const handleSubmit = () => {
    if (!categoryName) return;

    const payload = {
      _id: editData?._id,
      categoryId: editData?.categoryId || '',
      categoryIcon: selectedProp,
      categoryName,
    };

    if (editData) {
      dispatch(updateCategory({ category: payload }));
    } else {
      dispatch(createCategory({ category: payload }));
    }

    toggleDailog(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Icon Selection Section */}
      <Stack alignItems="center" spacing={2}>
        <Box sx={{ position: 'relative' }}>
          <Avatar
            src={selectedProp}
            variant="rounded"
            sx={{
              width: 120,
              height: 120,
              bgcolor: theme.palette.primary.main + '08',
              border: `2px dashed ${theme.palette.divider}`,
              borderRadius: '24px',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: theme.palette.primary.main,
                bgcolor: theme.palette.primary.main + '12',
              }
            }}
          >
            {!selectedProp && (
              <PhotoCameraBackTwoToneIcon 
                sx={{ fontSize: 40, color: theme.palette.text.secondary }} 
              />
            )}
          </Avatar>
          
          <Tooltip title="Change Icon">
            <IconButton
              onClick={() => setPropDialogOpen(true)}
              sx={{
                position: 'absolute',
                bottom: -10,
                right: -10,
                bgcolor: theme.palette.primary.main,
                color: '#fff',
                boxShadow: theme.shadows[4],
                '&:hover': { bgcolor: theme.palette.primary.dark }
              }}
              size="small"
            >
              <PhotoCameraBackTwoToneIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1 }}>
          CATEGORY VISUAL
        </Typography>
      </Stack>

      {/* Input Fields */}
      <Stack spacing={3}>
        <TextField
          autoFocus
          label="Category Name"
          placeholder="e.g. Main Course, Desserts"
          fullWidth
          variant="outlined"
          value={categoryName}
          onChange={(e) => setCategoryName(e.target.value)}
          InputProps={{
            sx: { borderRadius: '12px' }
          }}
        />
        
        <Box 
          sx={{ 
            p: 2, 
            borderRadius: '12px', 
            bgcolor: theme.palette.primary.main + '05',
            border: `1px solid ${theme.palette.primary.main}20`
          }}
        >
          <Typography variant="body2" color="primary" fontWeight={600} display="flex" alignItems="center" gap={1}>
            <CheckCircleIcon fontSize="small" /> 
            Pro Tip
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Use high-quality transparent PNGs for icons to make your digital menu look more professional.
          </Typography>
        </Box>
      </Stack>

      {/* Action Buttons */}
      <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
        <CommonBtn
          fullWidth
          variant="outlined"
          onClick={() => toggleDailog(false)}
          sx={{ borderRadius: '12px', py: 1.5 }}
        >
          Cancel
        </CommonBtn>
        <CommonBtn
          fullWidth
          variant="contained"
          onClick={handleSubmit}
          disabled={!categoryName}
          sx={{ borderRadius: '12px', py: 1.5 }}
        >
          {editData ? 'Update Category' : 'Save Category'}
        </CommonBtn>
      </Stack>

      {/* Icon Selector Instance */}
      <PropSelectorDialog
        open={propDialogOpen}
        onClose={() => setPropDialogOpen(false)}
        onSelect={(url) => {
          setSelectedProp(url);
          setPropDialogOpen(false);
        }}
      />
    </Box>
  );
};

export { AddCategory };